import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Settings } from 'lucide-react';
import { hasSubmodules } from '../data/specialtyStructure';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const Dashboard = ({ user, onLogout }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const navigate = useNavigate();

  const specialtyItems = [
    { id: 'ginecologia', name: 'Ginecología y Obstetricia' },
    { id: 'cirugia', name: 'Cirugía' },
    { id: 'pediatria', name: 'Pediatría' },
    { id: 'medicina-interna', name: 'Medicina Interna' },
    { id: 'otros', name: 'Otros' }
  ];

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const fetchQuizzes = async () => {
    try {
      const response = await apiClient.get('/quizzes');
      setQuizzes(response.data);
    } catch (error) {
      toast.error('Error al cargar los cuestionarios');
      console.error('Error fetching quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSpecialtyClick = (specialty) => {
    if (hasSubmodules(specialty)) {
      navigate(`/submodules/${encodeURIComponent(specialty)}`);
    } else {
      navigate(`/topics/${encodeURIComponent(specialty)}`);
    }
  };

  // Play pencil scribble sound
  const playPencilSound = () => {
    try {
      const audio = new Audio('https://customer-assets.emergentagent.com/job_13d67352-1aa7-430c-b46e-966b83d4fc8c/artifacts/wfi9fn7o_pencil_scribble_7-389260.mp3');
      audio.volume = 1.0;
      audio.play();
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  const handleItemClick = (specialty) => {
    playPencilSound();
    setTimeout(() => {
      handleSpecialtyClick(specialty);
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 py-4 px-4 z-50 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-black font-medium text-sm hover:text-gray-600 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Menú Principal</span>
          </button>
          
          <div className="flex items-center space-x-2">
            {user.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
              >
                <Settings size={14} className="text-black" />
              </button>
            )}
            
            <button
              onClick={onLogout}
              className="bg-white rounded-full p-1.5 border border-black hover:bg-gray-100 transition-all"
            >
              <LogOut size={14} className="text-black" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-16">
        {/* Title */}
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-12 text-center">
          Cuestionarios de las GPCs
        </h1>

        {/* Menu List with Circles */}
        <nav className="text-center">
          <ul className="space-y-6 sm:space-y-8">
            {specialtyItems.map((item, index) => (
              <li 
                key={item.id}
                className="transform transition-all duration-300"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <button
                  onClick={() => handleItemClick(item.name)}
                  onMouseEnter={() => setHoveredItem(item.id)}
                  onMouseLeave={() => setHoveredItem(null)}
                  data-testid={`specialty-card-${item.id}`}
                  className="group flex items-center justify-center space-x-4 text-xl sm:text-2xl lg:text-3xl font-medium text-gray-700 hover:text-black transition-colors duration-300"
                >
                  {/* Circle indicator */}
                  <span 
                    className={`relative w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-gray-400 transition-all duration-300 flex-shrink-0 ${
                      hoveredItem === item.id ? 'border-black' : ''
                    }`}
                  >
                    <span 
                      className={`absolute inset-0.5 rounded-full bg-black transition-all duration-300 ${
                        hoveredItem === item.id ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
                      }`}
                    ></span>
                  </span>
                  
                  {/* Text label */}
                  <span className="transition-all duration-300 group-hover:tracking-wide">
                    {item.name}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default Dashboard;
